import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import styles from './style/UserPage.module.css';
import UserPostsList from './partials/UserPostsList';
import { MessageModal, FollowModal } from './partials/UserPageModals'; 
import { useUserPageLogic } from './hooks/useUserPageLogic'; 

// [유틸리티] 날짜 포맷
const formatPeriod = (periodStr) => {
  if (!periodStr) return '';
  const [year, week] = periodStr.split('-W');
  return `${year}년 ${Number(week)}주차`;
};

// [NEW] 포트폴리오 카드 컴포넌트
const PortfolioCard = ({ item, navigate, t }) => {
  const isPositive = item.ret_1y >= 0;
  const returnColor = isPositive ? '#e74c3c' : '#3498db'; 

  return (
    <div className={styles.portfolioCard} onClick={() => navigate(`/portfolios/${item.portfolioId}`)}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{item.name || t('userPage.unnamed')}</h3>
        <span className={styles.cardDuration}>
          {t('userPage.day', { count: Math.floor((new Date() - new Date(item.createdAt)) / (86400000)) })}
        </span>
      </div>
      
      <div className={styles.cardMetrics}>
        <div className={styles.cardMetricItem}>
          {/* 번역 적용: Return (1Y) -> 수익률 (1년) */}
          <span className={styles.metricLabel}>{t('userPage.metrics.return1Y')}</span>
          <span className={styles.metricValue} style={{ color: returnColor }}>
            {isPositive ? '+' : ''}{((item.ret_1y ?? 0) * 100).toFixed(2)}%
          </span>
        </div>
        <div className={styles.metricDivider}></div>
        <div className={styles.cardMetricItem}>
          {/* 번역 적용: Sharpe -> 샤프 지수 */}
          <span className={styles.metricLabel}>{t('userPage.metrics.sharpe')}</span>
          <span className={styles.metricValue}>
            {(item.sharpe_1y ?? 0).toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
};

// "Top 10" 이내 부문별 전문가 뱃지 로직
const getAwardDetails = (item, t) => {
  const { rank_ret, rank_sharpe, rank_mdd, metrics } = item;
  const THRESHOLD = 10; 

  const candidates = [
    { 
      type: 'yield',
      rank: rank_ret, 
      label: t('userPage.metrics.yieldMaster'), // 번역 적용
      icon: '📈', 
      valueDisplay: `${(metrics?.ret?.['1w'] * 100).toFixed(2)}%`,
      cssClass: styles.typeYield 
    },
    { 
      type: 'sharpe',
      rank: rank_sharpe, 
      label: t('userPage.metrics.stabilityPro'), // 번역 적용
      icon: '🛡️', 
      valueDisplay: `Sharpe ${Number(metrics?.sharpe?.['1w']).toFixed(2)}`,
      cssClass: styles.typeStability 
    },
    { 
      type: 'mdd',
      rank: rank_mdd, 
      label: t('userPage.metrics.defenseExpert'), // 번역 적용
      icon: '🧱', 
      valueDisplay: `MDD ${(Number(metrics?.mdd?.['1w']) * 100).toFixed(2)}%`,
      cssClass: styles.typeDefense 
    }
  ];

  const qualified = candidates.filter(c => c.rank && c.rank > 0 && c.rank <= THRESHOLD);
  if (qualified.length === 0) return null;
  const best = qualified.reduce((prev, curr) => (prev.rank < curr.rank ? prev : curr));

  return {
    ...best, 
    // 번역 적용: {{label}} Top {{rank}}
    fullRankText: t('userPage.metrics.topRank', { label: best.label, rank: best.rank }) 
  };
};

export default function UserPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated } = useAuth();
  const { userId: pageOwnerId } = useParams();
  const currentUserId = authUser?.profile?.sub;

  const { state, stats, actions, loading, modals } = useUserPageLogic(pageOwnerId, currentUserId, isAuthenticated, authUser);
  const [activeTab, setActiveTab] = useState('posts');

  const sortedHistory = useMemo(() => {
    if (!state.rankingHistory) return [];
    return [...state.rankingHistory].sort((a, b) => b.period.localeCompare(a.period));
  }, [state.rankingHistory]);

  // getAwardDetails에 t 전달
  const bestRecordData = sortedHistory.length > 0 ? getAwardDetails(sortedHistory[0], t) : null;

  if (loading.profile) return <div className={styles.loadingContainer}>{t('userPage.loadingUser')}</div>;

  return (
    <div className={styles.container}>
      {/* --- 프로필 헤더 --- */}
      <section className={styles.profileHeader}>
        <div className={styles.headerContent}>
          <div className={styles.profileImageContainer}>
            {state.profileImageUrl ? (
              <img src={state.profileImageUrl} alt={t('userPage.profileImageAlt', { nickname: state.nickname })} className={styles.profileImage} />
            ) : (
              <div className={styles.profileImagePlaceholder}>
                 <span className={styles.placeholderText}>{state.nickname?.charAt(0).toUpperCase() || '?'}</span>
              </div>
            )}
          </div>
          
          <div className={styles.titleWrapper}>
            <h1 className={styles.profileTitle}>{state.nickname || t('userPage.userPageTitle')}</h1>
            
            {bestRecordData && (
              <div 
                className={`${styles.rankBadge} ${bestRecordData.cssClass}`}
                title={t('userPage.latestAchievement')} // 번역 적용
              >
                <span className={styles.badgeIcon}>{bestRecordData.icon}</span>
                <span className={styles.badgeText}>
                   {bestRecordData.fullRankText}
                </span>
              </div>
            )}
          </div>

          <div className={styles.profileStats}>
            <StatItem count={state.followerList.length} label={t('userPage.followerTitleNoCount')} onClick={() => modals.openFollow('followers')} />
            <span className={styles.statDivider}>|</span>
            <StatItem count={state.followingList.length} label={t('userPage.followingTitleNoCount')} onClick={() => modals.openFollow('following')} />
          </div>

          {currentUserId !== pageOwnerId && (
            <div className={styles.followButtonContainer}>
              <button 
                className={`${styles.actionBtn} ${state.isFollowing ? styles.unfollowBtn : styles.followBtn}`}
                onClick={state.isFollowing ? actions.unfollow : actions.follow}
                disabled={loading.follow}
              >
                <span className={styles.buttonIcon}>{state.isFollowing ? '➖' : '➕'}</span>
                {state.isFollowing ? t('userPage.unfollowButton') : t('userPage.followButton')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* --- 탭 --- */}
      <nav className={styles.tabs}>
        {['posts', 'about'].map(tab => (
          <button key={tab} className={`${styles.tabButton} ${activeTab === tab ? styles.activeTab : ''}`} onClick={() => setActiveTab(tab)}>
            {t(`userPage.tab${tab.charAt(0).toUpperCase() + tab.slice(1)}`)}
          </button>
        ))}
      </nav>

      {/* --- 컨텐츠 --- */}
      {activeTab === 'posts' ? (
        <UserPostsList userId={pageOwnerId} nickname={state.nickname} introduction={state.introduction} />
      ) : (
        <div className={styles.aboutContainer}>
          
          {/* 1. Hall of Fame */}
          {sortedHistory.some(item => getAwardDetails(item, t) !== null) && (
            <section className={styles.hallOfFame}>
              <div className={styles.hallOfFameHeader}>
                <h2 className={styles.hallOfFameTitle}>{t('userPage.hallOfFame')}</h2>
                <span className={styles.awardSummary}>{t('userPage.topPerformance')}</span>
              </div>
              
              <div className={styles.trophyScroller}>
                 <div className={styles.trophyGrid}>
                  {sortedHistory.map((history, index) => {
                     const award = getAwardDetails(history, t); // t 전달
                     if (!award) return null; 
                     return (
                      <div key={`${history.period}-${index}`} className={`${styles.trophyCard} ${award.cssClass}`}>
                        <div className={styles.trophyTopRow}>
                           <div className={styles.trophyIcon}>{award.icon}</div>
                           <div className={styles.rankNumber}>#{award.rank}</div>
                        </div>
                        <div className={styles.rankLabel}>{award.label}</div>
                        <div className={styles.metricValue}>{award.valueDisplay}</div>
                        <div className={styles.periodBadge}>{formatPeriod(history.period)}</div>
                      </div>
                     );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* 2. 2단 레이아웃: 투자 철학 & 핵심 지표 */}
          <div className={styles.twoColumnLayout}>
            <section className={styles.infoCard}>
              <h2 className={styles.cardHeading}>{t('userPage.aboutTitle')}</h2>
              <div className={styles.introductionBox}>
                 {state.introduction ? (
                   <p className={styles.introText}>{state.introduction}</p>
                 ) : (
                   <p className={styles.emptyText}>{t('userPage.noIntroduction')}</p>
                 )}
              </div>
            </section>

            <section className={styles.infoCard}>
              <h2 className={styles.cardHeading}>{t('userPage.investmentResults')}</h2>
              {loading.portfolio ? <p>{t('userPage.loadingPortfolios')}</p> : 
               stats.allPortfolios.length === 0 ? <p className={styles.emptyText}>{t('userPage.noPortfolios')}</p> : (
                <div className={styles.statsGrid}>
                  <div className={styles.statBox}>
                    <span className={styles.statLabel}>{t('userPage.avg1YReturn')}</span>
                    <span className={`${styles.statBigValue} ${stats.averageReturn >= 0 ? styles.plus : styles.minus}`}>
                      {((stats.averageReturn * 100).toFixed(2))}%
                    </span>
                  </div>
                  <div className={styles.statBox}>
                    <span className={styles.statLabel}>{t('userPage.avg1YSharpe')}</span>
                    <span className={styles.statBigValue}>{stats.averageSharpe.toFixed(2)}</span>
                  </div>
                </div>
              )}
            </section>
          </div>

          {/* 3. Portfolio List */}
          <section className={styles.portfolioSection}>
            <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>
                    {t('userPage.trackRecords')} <span className={styles.countBadge}>{stats.allPortfolios.length}</span>
                </h2>
            </div>
            
            {!loading.portfolio && stats.allPortfolios.length > 0 && (
              <div className={styles.portfolioGrid}>
                {stats.allPortfolios.map(p => (
                  <PortfolioCard key={p.portfolioId} item={p} navigate={navigate} t={t} />
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      {modals.alert.show && <MessageModal message={modals.alert.message} onClose={modals.closeAlert} onConfirm={modals.closeAlert} />}
      {modals.follow.show && (
        <FollowModal 
          title={modals.follow.type === 'followers' ? t('userPage.followerTitleNoCount') : t('userPage.followingTitleNoCount')}
          users={modals.follow.type === 'followers' ? state.followerList : state.followingList}
          onClose={modals.closeFollow}
          onUserClick={(id) => { modals.closeFollow(); navigate(`/users/${id}/profile`); }}
          t={t}
        />
      )}
    </div>
  );
}

const StatItem = ({ count, label, onClick }) => (
  <div className={styles.statItem} onClick={onClick} role="button" tabIndex={0}>
    <span className={styles.statCount}>{count}</span>
    <span className={styles.statLabel}>{label}</span>
  </div>
);